import React, { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import LiveStrip from "./LiveStrip";
import FloatingSupportChat from "./FloatingSupportChat";
import PremiumContactStrip from "./PremiumContactStrip";
import CcgRouteLhrStatusCompact from "./CcgRouteLhrStatusCompact";
import CcgPremiumReviews from "./CcgPremiumReviews";

const defaultTrustCards = [
  {
    icon: "✓",
    eyebrow: "Licensed operator",
    title: "Licensed & insured",
    body: "Professional airport transfers",
  },
  {
    icon: "G",
    eyebrow: "Google reviews",
    title: "4.8 ★★★★★",
    body: "Trusted by local passengers",
  },
];

const defaultQuotePromises = [
  "Fixed upfront fare",
  "Licensed drivers",
  "Secure online booking",
  "Instant confirmation",
];

function RouteHeroLiveBanner({ banner }) {
  if (!banner) return null;
  return (
    <div className="ccg-route-live-banner">
      {banner.kicker ? <span>{banner.kicker}</span> : null}
      {banner.title ? <strong>{banner.title}</strong> : null}
      {banner.body ? <p>{banner.body}</p> : null}
      {banner.cta ? <b>{banner.cta}</b> : null}
    </div>
  );
}

const defaultJsonLd = (route) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "TaxiService",
      "@id": `${route.seo?.canonical}#service`,
      name: route.seo?.serviceName || `CheapCabGrab – ${route.hero?.h1 || "Airport Taxi"}`,
      url: route.seo?.canonical,
      description: route.seo?.description,
      areaServed: (route.seo?.areaServed || []).map((name) => ({ "@type": "Place", name })),
      serviceType: "Fixed-price airport taxi transfer",
      provider: { "@type": "Organization", name: "CheapCabGrab", url: "https://www.cheapcabgrab.com" },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: route.seo?.offerCatalogName || `${route.hero?.h1 || "Airport Taxi"} Routes`,
        itemListElement: [{
          "@type": "Offer",
          name: route.seo?.offerName || route.hero?.h1 || "Fixed-price airport taxi",
          description: route.seo?.offerDescription || route.seo?.description,
          priceCurrency: "GBP",
        }],
      },
      potentialAction: {
        "@type": "ReserveAction",
        target: route.seo?.canonical,
        result: { "@type": "Reservation", name: route.seo?.reservationName || `Taxi booking for ${route.hero?.h1 || "airport route"}` },
      },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${route.seo?.canonical}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.cheapcabgrab.com" },
        { "@type": "ListItem", position: 2, name: route.seo?.routeName || route.hero?.h1, item: route.seo?.canonical },
      ],
    },
    ...(route.faqItems?.length ? [{
      "@type": "FAQPage",
      "@id": `${route.seo?.canonical}#faq`,
      mainEntity: route.faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    }] : []),
  ],
});

const GeneratedRouteContent = ({ route }) => {
  if (!route.generatedContent) return null;
  const content = route.generatedContent;
  return (
    <>
      <section className="route-section" id="route-overview">
        <header className="route-section-header">
          <h2>{content.overviewTitle}</h2>
          <p>{content.overviewIntro}</p>
        </header>
        <div className="route-overview-grid">
          {(content.overviewCards || []).map((card) => (
            <article key={card.title}>
              <h3>{card.title}</h3>
              {card.body ? <p>{card.body}</p> : null}
              {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
            </article>
          ))}
        </div>
      </section>

      <section className="route-section" id="route-planning">
        <header className="route-section-header">
          <h2>{content.planningTitle}</h2>
          <p>{content.planningIntro}</p>
        </header>
        <div className="route-areas-grid">
          {(content.planningCards || []).map((card) => (
            <article key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
            </article>
          ))}
        </div>
      </section>

      <section className="route-section" id="route-tools">
        <header className="route-section-header">
          <h2>Useful journey links</h2>
          <p>Check your terminal, compare the reverse route or plan a larger group transfer.</p>
        </header>
        <div className="route-links-grid">
          {(content.links || []).map((link) => (
            <article key={link.to}>
              <h3>{link.title}</h3>
              <p>{link.body}</p>
              <Link to={link.to} className="route-link-btn">{link.label}</Link>
            </article>
          ))}
        </div>
      </section>

      <section className="route-section" id="route-faq">
        <header className="route-section-header">
          <h2>{content.faqTitle}</h2>
          <p>Common questions about this CheapCabGrab airport taxi route.</p>
        </header>
        <div className="route-faq-grid">
          {(route.faqItems || []).map((item) => (
            <article className="route-faq-card" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
};

function CcgRouteSeo({ route }) {
  const seo = useMemo(() => route.seo || {}, [route.seo]);
  const canonical = seo.canonical;
  const title = seo.title;
  const description = seo.description;
  const jsonLd = seo.jsonLd || defaultJsonLd(route);
  const jsonLdId = route.id || "ccg-route";

  useEffect(() => {
    if (!canonical || !title) return undefined;

    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        el.setAttribute(key, value);
      });
    };

    const applySeo = () => {
      document.title = title;

      let canonicalEl = document.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);

      if (description) setMeta('meta[name="description"]', { name: "description", content: description });
      if (seo.keywords) setMeta('meta[name="keywords"]', { name: "keywords", content: seo.keywords });
      setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      setMeta('meta[property="og:title"]', { property: "og:title", content: seo.ogTitle || title });
      if (seo.ogDescription || description) {
        setMeta('meta[property="og:description"]', {
          property: "og:description",
          content: seo.ogDescription || description,
        });
      }
      setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });

      let script = document.querySelector(`script[data-ccg-route-jsonld="${jsonLdId}"]`);
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-ccg-route-jsonld", jsonLdId);
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    };

    applySeo();
    const timers = [window.setTimeout(applySeo, 0), window.setTimeout(applySeo, 750)];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [canonical, description, jsonLd, jsonLdId, seo, title]);

  return (
    <Helmet>
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      {seo.keywords ? <meta name="keywords" content={seo.keywords} /> : null}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.ogTitle || title} />
      {seo.ogDescription || description ? <meta property="og:description" content={seo.ogDescription || description} /> : null}
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}

const CcgRoutePremiumPage = ({ route, QuoteComponent }) => {
  const hero = route.hero || {};
  const quote = route.quote || {};
  const trustCards = route.trustCards || defaultTrustCards;
  const quotePromises = route.quotePromises || defaultQuotePromises;

  const scrollToForm = () => {
    const el = document.getElementById("route-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="route-page ccg-route-premium">
      <CcgRouteSeo route={route} />
      <LiveStrip variant={route.liveStripVariant || "slough"} />

      <main className="route-main">
        <section className="ccg-route-hero">
          <div className="ccg-route-hero-shell">
            <div className="ccg-route-hero-top">
              <div className="ccg-route-hero-copy">
                <span className="ccg-route-eyebrow">{hero.eyebrow}</span>
                <h1>{hero.h1}</h1>
                {hero.subtitle ? <p>{hero.subtitle}</p> : null}
                {hero.body ? <p>{hero.body}</p> : null}
              </div>

              <div className="ccg-route-trust-cards" aria-label="CheapCabGrab route trust signals">
                {trustCards.map((card) => (
                  <article className="ccg-route-trust-card" key={`${card.eyebrow}-${card.title}`}>
                    <b>{card.icon}</b>
                    <span>
                      <small>{card.eyebrow}</small>
                      <strong>{card.title}</strong>
                      <span>{card.body}</span>
                    </span>
                  </article>
                ))}
              </div>
            </div>

            <div className="ccg-route-quote-wrap" id="route-quote-form">
              <RouteHeroLiveBanner banner={route.heroLiveBanner} />
              <div className="ccg-route-quote-head">
                <span className="ccg-route-quote-label">{quote.label || "Get instant quotes"}</span>
                <span className="ccg-route-quote-note"><i /> {quote.note || "Free • Fast • No obligation"}</span>
              </div>
              <QuoteComponent
                presetPickup={quote.presetPickup || ""}
                presetDropoff={quote.presetDropoff || ""}
                pickupPlaceholder={quote.pickupPlaceholder || "Pickup postcode"}
                dropoffPlaceholder={quote.dropoffPlaceholder || "Drop-off postcode"}
              />
              <ul className="ccg-route-quote-promises">
                {quotePromises.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <PremiumContactStrip />
            </div>

            <ul className="ccg-route-flags">
              {(route.flags || []).map((item) => <li key={item}>{item}</li>)}
            </ul>

            <div className="ccg-route-review-strip">
              <CcgPremiumReviews pageKey={route.reviewPageKey || route.path || "/"} ariaLabel={route.reviewAriaLabel || "Route customer reviews"} />
            </div>
          </div>
        </section>

        {!route.hideLiveStatus ? (
          <>
            {route.statusIntro ? <p className="ccg-route-status-intro">{route.statusIntro}</p> : null}
            <CcgRouteLhrStatusCompact
              title={route.lhrStatusTitle || "Live Heathrow checks for this taxi route" }
              kicker={route.lhrStatusKicker || "Live Heathrow intelligence"}
              statusProfile={route.statusProfile || ""}
            />
          </>
        ) : null}

        <GeneratedRouteContent route={route} />

        {(route.sections || []).map((section) => (
          <section className="route-section" id={section.id} key={section.id || section.title}>
            <header className="route-section-header">
              <h2>{section.title}</h2>
              {section.body ? <p>{section.body}</p> : null}
            </header>
            {section.render ? section.render() : null}
          </section>
        ))}

        <button className="route-floating-book-btn" onClick={scrollToForm}>
          {route.floatingCta || "🚖 Book Taxi"}
        </button>

        <div className="route-bottom-cta">
          <button className="route-bottom-cta-btn" onClick={scrollToForm}>
            {route.mobileCta || route.floatingCta || "🚖 Book Taxi"}
          </button>
        </div>

        <div style={{ height: "40px" }} />
      </main>

      <FloatingSupportChat
        brand="ccg"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={9000}
        nudgeText={route.supportNudge || "Need taxi help?"}
      />
    </div>
  );
};

export default CcgRoutePremiumPage;
